/**
 * OnayMetinleriPage.jsx — Sistem Ayarları › Onay Metinleri (backlog #536, 2026-10-21)
 * Route: /Management/SistemAyarlari/OnayMetinleri   İzin: page.sistem (okumalar personel; yazmalar onay_metni.manage)
 *
 * Onay metinlerinin (KVKK, giriş kuralları, denetim kuralları, taahhütnameler) listesi. Her satır bir metin: sayfaların
 * istediği ANAHTAR, başlık, kitle, güncel yayınlanmış sürüm ile en yeni taslağın hangi dillerde (TR / EN) yazılı olduğu
 * ve kaç kişinin onayladığı. Metnin kendisi ve sürümleri ayrıntı sayfasında (OnayMetniDetayPage). Yeni bir metnin hangi
 * sayfada görüneceği şimdilik elle bağlanır: anahtarı listeden okuyup ön yüze bildirin.
 * Sözleşme: docs/guides/FE-OnayMetniController-guide.md.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Table, Button, Alert, Spinner, Modal, ModalHeader, ModalBody, ModalFooter,
  Label, Input, Row, Col, FormGroup,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import i18n from "../../../i18n";
import { useYetki, PERMISSION_KEYS } from "../../../services/rbacService";
import {
  listOnayMetinleri, createOnayMetni, HEDEF, OLUSTURULABILIR_HEDEFLER, ANAHTAR_DESENI, YASAK_ANAHTARLAR,
} from "../../../services/onayMetniService";
import { DilIsaretleri, metinBasligi, tumBasliklar, hedefAdi, onaySayisi } from "./OnayMetniParcalari";

const YOL = "/Management/SistemAyarlari/OnayMetinleri";

// ─── Yeni metin ─────────────────────────────────────────────────────────────
function YeniMetinModal({ acik, kapat, metinler, onOlusturuldu }) {
  const { t } = useTranslation("ayarlar");
  const [anahtar, setAnahtar] = useState("");
  const [hedef, setHedef] = useState(String(HEDEF.KULLANICI));
  const [zorunlu, setZorunlu] = useState(true);
  const [ekNot, setEkNot] = useState(false);
  const [sira, setSira] = useState("");
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState(null);

  const temiz = anahtar.trim();
  const sonSira = metinler.reduce((m, x) => Math.max(m, Number(x.sortOrder) || 0), 0);
  const anahtarHatasi = (() => {
    if (!temiz) return null;
    if (!ANAHTAR_DESENI.test(temiz)) return t("onayMetinleri.anahtarDeseni");
    if (YASAK_ANAHTARLAR.includes(temiz)) return t("onayMetinleri.anahtarYasak", { v: temiz });
    if (metinler.some((x) => x.onayMetniKey === temiz)) return t("onayMetinleri.anahtarVar", { v: temiz });
    return null;
  })();

  function sifirla() {
    setAnahtar(""); setHedef(String(HEDEF.KULLANICI)); setZorunlu(true); setEkNot(false); setSira(""); setHata(null);
  }

  async function gonder() {
    setGonderiliyor(true);
    setHata(null);
    try {
      await createOnayMetni({
        onayMetniKey: temiz, hedef: Number(hedef), isRequired: zorunlu, sortOrder: sira === "" ? sonSira + 1 : Number(sira),
        ekNot: ekNot && Number(hedef) === HEDEF.KULLANICI,
      });
      sifirla();
      onOlusturuldu(temiz);
    } catch (e) {
      setHata(e.message);
    } finally {
      setGonderiliyor(false);
    }
  }

  return (
    <Modal isOpen={acik} toggle={() => !gonderiliyor && kapat()} centered>
      <ModalHeader toggle={() => !gonderiliyor && kapat()}>{t("onayMetinleri.yeniMetin")}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="om-anahtar" className="small mb-1">{t("onayMetinleri.anahtar")}</Label>
          <Input id="om-anahtar" bsSize="sm" className="onay-anahtar" value={anahtar} maxLength={50} autoFocus
            invalid={!!anahtarHatasi} placeholder="ornek_kural" onChange={(e) => setAnahtar(e.target.value)} />
          <div className={`small mt-1 ${anahtarHatasi ? "text-danger" : "text-muted"}`}>
            {anahtarHatasi || t("onayMetinleri.anahtarYardim")}
          </div>
        </FormGroup>
        <Row className="g-2">
          <Col xs={7}>
            <FormGroup>
              <Label for="om-hedef" className="small mb-1">{t("onayMetinleri.kitle")}</Label>
              <Input id="om-hedef" type="select" bsSize="sm" value={hedef} onChange={(e) => setHedef(e.target.value)}>
                {OLUSTURULABILIR_HEDEFLER.map((h) => <option key={h} value={h}>{hedefAdi(t, h)}</option>)}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={5}>
            <FormGroup>
              <Label for="om-sira" className="small mb-1">{t("onayMetinleri.sira")}</Label>
              <Input id="om-sira" type="number" bsSize="sm" value={sira} placeholder={String(sonSira + 1)} onChange={(e) => setSira(e.target.value)} />
            </FormGroup>
          </Col>
        </Row>
        <div className="form-check mb-1">
          <Input id="om-zorunlu" type="checkbox" className="form-check-input" checked={zorunlu} onChange={(e) => setZorunlu(e.target.checked)} />
          <Label for="om-zorunlu" className="form-check-label small">{t("onayMetinleri.zorunlu")}</Label>
        </div>
        {Number(hedef) === HEDEF.KULLANICI && (
          <div className="form-check mb-1">
            <Input id="om-eknot" type="checkbox" className="form-check-input" checked={ekNot} onChange={(e) => setEkNot(e.target.checked)} />
            <Label for="om-eknot" className="form-check-label small">{t("onayMetinleri.ekNotKutusu")}</Label>
          </div>
        )}
        <div className="text-muted small mt-2">{t("onayMetinleri.yeniMetinNot")}</div>
        {hata && <Alert color="danger" className="py-2 small mt-3 mb-0">{hata}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={gonderiliyor}>{t("onayMetinleri.vazgec")}</Button>
        <Button color="primary" onClick={gonder} disabled={gonderiliyor || !temiz || !!anahtarHatasi}>
          {gonderiliyor && <Spinner size="sm" className="me-1" />}{t("onayMetinleri.olustur")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Sayfa ──────────────────────────────────────────────────────────────────
export default function OnayMetinleriPage() {
  const { t } = useTranslation("ayarlar");
  const navigate = useNavigate();
  const yazabilir = useYetki(PERMISSION_KEYS.ONAY_METNI_MANAGE) === true;
  const dil = i18n.language === "en" ? "en" : "tr";
  const [metinler, setMetinler] = useState(null);
  const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
  const [filtre, setFiltre] = useState({ ara: "", hedef: "", yalnizEtkin: false });
  const [yeniAcik, setYeniAcik] = useState(false);
  const [kopyalanan, setKopyalanan] = useState(null);

  useEffect(() => { document.title = i18n.t("ayarlar:onayMetinleri.sayfaBasligi"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await listOnayMetinleri();
        if (!ignore) { setMetinler(list); setYuklemeHatasi(null); }
      } catch (e) {
        if (!ignore) { setYuklemeHatasi(e.message); setMetinler([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const gorunen = useMemo(() => {
    const ara = filtre.ara.trim().toLowerCase();
    return (metinler || []).filter((m) =>
      (!filtre.hedef || String(m.hedef) === filtre.hedef)
      // #625 (backend #624): "etkin" = genel hat açık YA DA bir fuarın kendi sürümü yürürlükte (sunucunun onlyActive'i ile aynı)
      && (!filtre.yalnizEtkin || m.isActive || m.etkinFuarSayisi > 0)
      // anahtar ya da herhangi bir dildeki başlık (güncel ve taslak)
      && (!ara || m.onayMetniKey.includes(ara) || tumBasliklar(m).some((b) => b.toLowerCase().includes(ara))));
  }, [metinler, filtre]);

  async function anahtarKopyala(anahtar) {
    try {
      await navigator.clipboard.writeText(anahtar);
      setKopyalanan(anahtar);
      setTimeout(() => setKopyalanan((k) => (k === anahtar ? null : k)), 1500);
    } catch {
      // Pano kapalıysa sessizce geç: anahtar zaten ekranda yazılı.
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("onayMetinleri.baslik")} pageTitle={t("onayMetinleri.sistemAyarlari")} />

        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-end">
              <Col md={4} lg={3}>
                <Label for="om-ara" className="small mb-1">{t("onayMetinleri.ara")}</Label>
                <Input id="om-ara" bsSize="sm" value={filtre.ara} placeholder={t("onayMetinleri.araYer")}
                  onChange={(e) => setFiltre((f) => ({ ...f, ara: e.target.value }))} />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <Label for="om-f-hedef" className="small mb-1">{t("onayMetinleri.kitle")}</Label>
                <Input id="om-f-hedef" type="select" bsSize="sm" value={filtre.hedef} onChange={(e) => setFiltre((f) => ({ ...f, hedef: e.target.value }))}>
                  <option value="">{t("common:tumu")}</option>
                  {[HEDEF.KULLANICI, HEDEF.KISI, HEDEF.TAAHHUTNAME, HEDEF.TALEP].map((h) => <option key={h} value={h}>{hedefAdi(t, h)}</option>)}
                </Input>
              </Col>
              <Col xs="auto" className="d-flex align-items-center pb-1">
                <div className="form-check mb-0">
                  <Input id="om-f-etkin" type="checkbox" className="form-check-input" checked={filtre.yalnizEtkin}
                    onChange={(e) => setFiltre((f) => ({ ...f, yalnizEtkin: e.target.checked }))} />
                  <Label for="om-f-etkin" className="form-check-label small">{t("onayMetinleri.yalnizcaEtkin")}</Label>
                </div>
              </Col>
              <Col xs="auto" className="ms-auto">
                {/* Kapalı düğme fare olaylarını yutar: ipucu sarmalayıcıda durur. */}
                <span title={yazabilir ? undefined : t("onayMetinleri.yazmaYetkisi")}>
                  <Button color="primary" size="sm" disabled={!yazabilir} onClick={() => setYeniAcik(true)} data-testid="yeni-metin">
                    <i className="bx bx-plus me-1" />{t("onayMetinleri.yeniMetin")}
                  </Button>
                </span>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}
        {metinler == null ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : gorunen.length === 0 ? (
          !yuklemeHatasi && (
            <div className="text-muted">{metinler.length === 0 ? t("onayMetinleri.henuzMetinYok") : t("onayMetinleri.suzgeceUyanYok")}</div>
          )
        ) : (
          <Card>
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table className="align-middle mb-0" size="sm" hover>
                  <thead className="text-muted small">
                    <tr>
                      <th className="ps-3">{t("onayMetinleri.anahtar")}</th>
                      <th>{t("onayMetinleri.metinBasligi")}</th>
                      <th>{t("onayMetinleri.kitle")}</th>
                      <th>{t("onayMetinleri.guncelSurum")}</th>
                      <th>{t("onayMetinleri.taslak")}</th>
                      <th className="text-end">{t("onayMetinleri.onay")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {gorunen.map((m) => {
                      const onay = onaySayisi(m);
                      const baslik = metinBasligi(m, dil);
                      return (
                        <tr key={m.onayMetniKey} className={m.isActive || m.etkinFuarSayisi > 0 ? "" : "text-muted"} data-testid={`metin-${m.onayMetniKey}`}>
                          <td className="ps-3 text-nowrap">
                            <Link to={`${YOL}/${m.onayMetniKey}`} className="onay-anahtar">{m.onayMetniKey}</Link>
                            <Button color="link" size="sm" className="p-0 ms-2 align-baseline" title={t("onayMetinleri.anahtariKopyala")}
                              onClick={() => anahtarKopyala(m.onayMetniKey)}>
                              <i className={`bx ${kopyalanan === m.onayMetniKey ? "bx-check" : "bx-copy"}`} />
                            </Button>
                          </td>
                          <td style={{ maxWidth: 280 }}>
                            <div className="text-truncate" title={baslik || ""}>{baslik || "—"}</div>
                            <div className="small text-muted">
                              {m.isRequired ? t("onayMetinleri.zorunluKisa") : t("onayMetinleri.istege")}
                              {/* "Pasif" = genel hat kapalı; fuarların kendi hatları ayrıca sayılır (#625) */}
                              {!m.isActive && ` · ${t("onayMetinleri.pasif")}`}
                              {m.ekNot && ` · ${t("onayMetinleri.ekNotu")}`}
                              {m.fuarSurumuOlanFuarSayisi > 0 && ` · ${t("onayMetinleri.fuarSurumleri", { n: m.fuarSurumuOlanFuarSayisi })}`}
                              {!m.isActive && m.etkinFuarSayisi > 0 && ` · ${t("onayMetinleri.etkinFuarSayisi", { n: m.etkinFuarSayisi })}`}
                              {m.kapaliFuarSayisi > 0 && ` · ${t("onayMetinleri.kapaliFuarSayisi", { n: m.kapaliFuarSayisi })}`}
                            </div>
                          </td>
                          <td>{hedefAdi(t, m.hedef)}</td>
                          <td className="text-nowrap">
                            {m.guncelSurumNo == null ? (
                              <span className="text-muted">{t("onayMetinleri.yayinlanmadi")}</span>
                            ) : (
                              <span className="d-inline-flex align-items-center gap-2">
                                <span className="onay-isaret onay-isaret--yayinda">v{m.guncelSurumNo}</span>
                                <DilIsaretleri diller={m.guncelDiller} />
                              </span>
                            )}
                          </td>
                          <td className="text-nowrap">
                            {m.taslakSayisi > 0 ? (
                              <span className="d-inline-flex align-items-center gap-2">
                                <span className="onay-isaret onay-isaret--taslak">v{m.taslakSurumNo}</span>
                                <DilIsaretleri diller={m.taslakDiller} />
                                {m.taslakSayisi > 1 && <span className="small text-muted">+{m.taslakSayisi - 1}</span>}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="text-end">{onay == null ? "—" : onay}</td>
                          <td className="text-end pe-3">
                            <Button color="light" size="sm" onClick={() => navigate(`${YOL}/${m.onayMetniKey}`)}>
                              {t("onayMetinleri.ac")}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        <YeniMetinModal
          acik={yeniAcik}
          kapat={() => setYeniAcik(false)}
          metinler={metinler || []}
          onOlusturuldu={(anahtar) => { setYeniAcik(false); navigate(`${YOL}/${anahtar}`); }}
        />
      </Container>
    </div>
  );
}
